import React, { useState } from 'react';
import { Card, Space, Typography, Form, Input, Button, Upload, Image, message, List, Tag, Modal, Popconfirm, Select } from 'antd';
import { PlusOutlined, LikeOutlined, CopyOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useQuery } from 'react-query';
import { useAuthStore } from '../../stores/authStore';

const { Title, Text } = Typography;
const TYPE_OPTIONS_MAP = {
  'new-material-process': ['新材料','新工艺','多媒体','艺术品','展柜','其它'],
  _default: ['通史','史前','红色','非遗','成就','自然','军事','专题','平面','户外','其它']
};

const ForumCategory = ({ category, title }) => {
  const { user, token } = useAuthStore();
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [imageUrls, setImageUrls] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [editForm] = Form.useForm();
  const [selectedType, setSelectedType] = useState('');
  const typeOptions = TYPE_OPTIONS_MAP[category] || TYPE_OPTIONS_MAP._default;

  const isUrl = (str) => {
    if (typeof str !== 'string') return false;
    try {
      const u = new URL(str);
      return u.protocol === 'http:' || u.protocol === 'https:';
    } catch {
      return false;
    }
  };

  // 用户积分
  const { data: pointsData, refetch: refetchPoints } = useQuery(['forumPoints'], async () => {
    const res = await axios.get('/forum/points/me');
    return res.data;
  });

  // 列表数据
  const { data: postData, refetch } = useQuery(['forumPosts', category, selectedType], async () => {
    const params = { category };
    if (selectedType) params.type = selectedType;
    const res = await axios.get('/forum/posts', { params });
    return res.data;
  });

  const onUploadChange = ({ file, fileList: newList }) => {
    setFileList(newList);
    if (file.status === 'done') {
      const url = file.response?.url;
      if (url) {
        setImageUrls(prev => Array.from(new Set([...prev, url])));
      }
      message.success('图片上传成功');
    } else if (file.status === 'error') {
      const errMsg = file.response?.message || '图片上传失败';
      message.error(errMsg);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    form.resetFields();
    setFileList([]);
    setImageUrls([]);
  };

  const handleCreatePost = async (values) => {
    try {
      const payload = {
        title: values.title,
        content: values.content,
        category,
        imageUrls,
        type: values.type
      };
      const res = await axios.post('/forum/posts', payload);
      if (res.data?.success) {
        message.success('发布成功');
        form.resetFields();
        setFileList([]);
        setImageUrls([]);
        refetch();
        refetchPoints();
        handleCloseModal();
      } else {
        message.error(res.data?.message || '发布失败');
      }
    } catch (e) {
      message.error(e?.response?.data?.message || '发布失败');
    }
  };

  const handleLikePost = async (item) => {
    try {
      const resp = await axios.post(`/forum/posts/${item.id}/like`);
      const applied = !!resp?.data?.rewardApplied;
      if (applied) {
        message.success('点赞成功，作者积分+5');
        if (user?.id === item.createdBy) {
          // 当前用户是作者，理论上作者点赞不加分；若积分变化则刷新
          refetchPoints();
        }
      } else {
        message.success('作者点赞不加分');
      }
    } catch (e) {
      message.error(e?.response?.data?.message || '点赞失败');
    }
  };

  // 复制内容并奖励作者（AI设计保留复制）
  const copyText = async (text) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      try { document.execCommand('copy'); } finally { document.body.removeChild(ta); }
    }
  };

  const handleCopyContent = async (item) => {
    try {
      await copyText(item.content);
      const resp = await axios.post(`/forum/posts/${item.id}/copy-reward`);
      const applied = !!resp?.data?.rewardApplied;
      if (applied) {
        message.success('内容已复制，作者积分+2');
        if (user?.id === item.createdBy) {
          refetchPoints();
        }
      } else {
        message.success('内容已复制（作者复制不加分）');
      }
    } catch (e) {
      message.error(e?.response?.data?.message || '复制或奖励失败');
    }
  };

  const openEditModal = (item) => {
    setEditingPost(item);
    setIsEditModalOpen(true);
    editForm.setFieldsValue({ title: item.title, content: item.content, type: item.type || '其它' });
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingPost(null);
    editForm.resetFields();
  };

  const handleUpdatePost = async (values) => {
    if (!editingPost) return;
    try {
      const res = await axios.put(`/forum/posts/${editingPost.id}`, { title: values.title, content: values.content, type: values.type });
      if (res.data?.success) {
        message.success('修改成功');
        refetch();
        handleCloseEditModal();
      } else {
        message.error(res.data?.message || '修改失败');
      }
    } catch (e) {
      message.error(e?.response?.data?.message || '修改失败');
    }
  };

  const handleDeletePost = async (item) => {
    try {
      const res = await axios.delete(`/forum/posts/${item.id}`);
      if (res.data?.success) {
        message.success('删除成功');
        refetch();
      } else {
        message.error(res.data?.message || '删除失败');
      }
    } catch (e) {
      message.error(e?.response?.data?.message || '删除失败');
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={3} style={{ margin: 0 }}>{title}</Title>
          <Space>
            <Card size="small">
              <Space>
                <Text>我的积分：</Text>
                <Tag color="blue">{pointsData?.points ?? 0}</Tag>
              </Space>
            </Card>
            <Button type="primary" onClick={() => setIsModalOpen(true)}>发帖</Button>
          </Space>
        </div>

        <Modal title="发布新帖" open={isModalOpen} onCancel={handleCloseModal} footer={null} destroyOnClose>
          <Form 
            form={form} 
            layout="vertical" 
            onFinish={handleCreatePost}
            initialValues={{ title: category === 'ai-design' ? 'https://' : '', type: '其它' }}
          >
            <Form.Item 
              label={category === 'ai-design' ? '网址' : '标题'} 
              name="title" 
              rules={[
                { required: true, message: category === 'ai-design' ? '请输入网址' : '请输入标题' },
                ...(category === 'ai-design' ? [{
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    return isUrl(value) 
                      ? Promise.resolve() 
                      : Promise.reject(new Error('请输入以 http:// 或 https:// 开头的合法网址'));
                  }
                }] : [])
              ]}
            > 
             <Input placeholder={category === 'ai-design' ? '请输入网址' : '请输入标题'} allowClear />
            </Form.Item>
            <Form.Item 
              label="类型" 
              name="type" 
              rules={[{ required: true, message: '请选择类型' }]}>
              <Select 
                placeholder="请选择类型"
                options={typeOptions.map(t => ({ value: t, label: t }))}
              />
            </Form.Item>
            <Form.Item 
              label={category === 'ai-design' ? '提示词' : '内容'} 
              name="content" 
              rules={[{ required: true, message: category === 'ai-design' ? '请输入提示词' : '请输入内容' }]}> 
              <Input.TextArea 
                rows={4} 
                placeholder={category === 'ai-design' ? '请输入提示词' : '请输入内容'} 
                allowClear 
              />
            </Form.Item>
            <Form.Item label="插图（自动压缩≤800KB/张）">
              <Upload
                listType="picture-card"
                multiple
                maxCount={9}
                fileList={fileList}
                onChange={onUploadChange}
                action="/api/forum/upload-image"
                name="image"
                headers={token ? { Authorization: `Bearer ${token}` } : {}}
                accept="image/*"
              >
                {fileList.length >= 9 ? null : (
                  <div>
                    <PlusOutlined />
                    <div style={{ marginTop: 8 }}>上传</div>
                  </div>
                )}
              </Upload>
              {imageUrls.length > 0 && (
                <Image.PreviewGroup>
                  <Space wrap>
                    {imageUrls.map((url, idx) => (
                      <Image key={idx} src={url} width={160} height={120} style={{ objectFit: 'cover' }} />
                    ))}
                  </Space>
                </Image.PreviewGroup>
              )}
            </Form.Item>
            <Form.Item>
              <Space>
                <Button onClick={handleCloseModal}>取消</Button>
                <Button type="primary" htmlType="submit">发布</Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        <Modal title="编辑帖子" open={isEditModalOpen} onCancel={handleCloseEditModal} footer={null} destroyOnClose>
          <Form form={editForm} layout="vertical" onFinish={handleUpdatePost}>
            <Form.Item 
              label={category === 'ai-design' ? '网址' : '标题'} 
              name="title" 
              rules={[
                { required: true, message: category === 'ai-design' ? '请输入网址' : '请输入标题' },
                ...(category === 'ai-design' ? [{
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    return isUrl(value) 
                      ? Promise.resolve() 
                      : Promise.reject(new Error('请输入以 http:// 或 https:// 开头的合法网址'));
                  }
                }] : [])
              ]}
            > 
              <Input placeholder={category === 'ai-design' ? '请输入网址' : '请输入标题'} allowClear />
            </Form.Item>
            <Form.Item 
              label="类型" 
              name="type" 
              rules={[{ required: true, message: '请选择类型' }]}>
              <Select 
                placeholder="请选择类型"
                options={typeOptions.map(t => ({ value: t, label: t }))}
              />
            </Form.Item>
            <Form.Item 
              label={category === 'ai-design' ? '提示词' : '内容'} 
              name="content" 
              rules={[{ required: true, message: category === 'ai-design' ? '请输入提示词' : '请输入内容' }]}> 
              <Input.TextArea 
                rows={4} 
                placeholder={category === 'ai-design' ? '请输入提示词' : '请输入内容'} 
                allowClear 
              />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button onClick={handleCloseEditModal}>取消</Button>
                <Button type="primary" htmlType="submit">保存</Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        <Card 
          title="最新帖子" 
          className="status-card"
          extra={(
            <Space size={8} align="center">
              <Text>类型：</Text>
              <Select 
                size="small"
                value={selectedType}
                style={{ minWidth: 120 }}
                onChange={(val) => setSelectedType(val)}
                options={[{ value: '', label: '全部' }, ...typeOptions.map(t => ({ value: t, label: t }))]}
              />
            </Space>
          )}
        >
          <List
            itemLayout="vertical"
            dataSource={postData?.posts || []}
            renderItem={(item) => (
              <List.Item key={item.id}
                extra={
                  item.imageUrls?.length ? (
                    (() => {
                      const urls = item.imageUrls || [];
                      const shown = urls.slice(0, 3);
                      const remaining = Math.max(0, urls.length - 3);
                      return (
                        <Space align="center" wrap>
                          <Image.PreviewGroup>
                            <Space wrap>
                              {shown.map((url, idx) => (
                                <Image key={idx} src={url} width={160} height={120} style={{ objectFit: 'cover' }} />
                              ))}
                            </Space>
                          </Image.PreviewGroup>
                          {remaining > 0 && (
                            <Text type="secondary" style={{ fontSize: 14 }}>… +{remaining}</Text>
                          )}
                        </Space>
                      );
                    })()
                  ) : null
                }
              >
                <List.Item.Meta
                  title={(
                    <Space size={8} align="center">
                      {item.type ? <Tag color="geekblue">{item.type}</Tag> : null}
                      {(category === 'ai-design' && isUrl(item.title))
                        ? (<Typography.Link href={item.title} target="_blank" rel="noopener noreferrer">{item.title}</Typography.Link>)
                        : item.title}
                    </Space>
                  )}
                  description={<Text type="secondary">由 {item.creatorName || item.createdBy} 发布于 {new Date(item.createdAt).toLocaleString()}</Text>}
                />
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  {category === 'ai-design' ? (
                    <Button
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() => handleCopyContent(item)}
                    >复制</Button>
                  ) : (
                    <Button
                      size="small"
                      icon={<LikeOutlined />}
                      onClick={() => handleLikePost(item)}
                    >点赞</Button>
                  )}
                  {user?.id === item.createdBy && (
                    <Button size="small" onClick={() => openEditModal(item)}>编辑</Button>
                  )}
                  {(user?.id === item.createdBy || user?.role === 'admin') && (
                    <Popconfirm title="确认删除该帖子？" okText="删除" cancelText="取消" onConfirm={() => handleDeletePost(item)}>
                      <Button size="small" danger>删除</Button>
                    </Popconfirm>
                  )}
                  <div style={{ whiteSpace: 'pre-wrap' }}>{item.content}</div>
                </div>
              </List.Item>
            )}
          />
        </Card>
      </Space>
    </div>
  );
};

export default ForumCategory;